import { TResponsiveTableRow } from '../../../../../components/common/generic/responsiveTable/types';
import { TApiaFinderFilter } from '../../../types';
import { makeListThunk } from '../../../../listSlice/api/util/util';
import notifyRequiredFilters from '../../util/notifyRequiredFilters';
import ApiaApi, { makeApiaUrl } from '../../../../../api/apiaApi/apiaApi';
import {
  TApiaFunction,
  TApiaFunctionPageInfo,
  TApiaLoad,
} from '../../../../../types';

export type TTableRows = {
  rows: TResponsiveTableRow[];
};

export type TFiltersRowsAndNewPage = {
  filters: TApiaFinderFilter[] | undefined;
  rows: TResponsiveTableRow[];
  newPageInfo: TApiaFunctionPageInfo;
};

export type TFiltersAndRequired = {
  filters: TApiaFinderFilter[] | undefined;
  areRequiredFilters: boolean;
};

export type TJustFilters = {
  filters: TApiaFinderFilter[] | undefined;
};

export function isJustFilters(
  returnValue: TFiltersRowsAndNewPage | TFiltersAndRequired | TJustFilters,
): returnValue is TJustFilters {
  const casted = returnValue as unknown as Record<string, unknown>;
  return !casted.rows && !casted.newPageInfo && !casted.areRequiredFilters;
}

export function isFiltersAndRequired(
  returnValue: TFiltersRowsAndNewPage | TFiltersAndRequired | TJustFilters,
): returnValue is TFiltersAndRequired {
  const casted = returnValue as unknown as Record<string, unknown>;
  return !!casted.filters && casted.areRequiredFilters !== undefined;
}

export function isFiltersRowsAndNewPage(
  returnValue: TFiltersRowsAndNewPage | TFiltersAndRequired | TJustFilters,
): returnValue is TFiltersRowsAndNewPage {
  const casted = returnValue as unknown as Record<string, unknown>;
  return casted.filters !== undefined && casted.newPageInfo !== undefined;
}

const deleteFilters = makeListThunk<
  TFiltersRowsAndNewPage | TFiltersAndRequired | TJustFilters
>('querySlice/deleteFilters', async ({ listId, workingMode }, thunkApi) => {
  const state = thunkApi.getState().querySlice.queries[listId];

  const emptyFilters: TApiaFinderFilter[] =
    state.filters?.map((currentFilter) => {
      if (
        currentFilter.apiaFilter.readonly ||
        currentFilter.finderFilter?.readonly
      ) {
        return currentFilter;
      }
      if (
        ['D', 'date'].includes(currentFilter.apiaFilter.type as string) ||
        currentFilter.apiaFilter.isRange
      ) {
        return {
          ...currentFilter,
          apiaFilter: {
            ...currentFilter.apiaFilter,
            deleteFiltersTimestamp: Date.now(),
            currentValue: '',
            filterToValue: '',
            savedValue: '',
          },
        };
      }
      if (currentFilter.apiaFilter.options) {
        const newFilter: TApiaFinderFilter = {
          ...currentFilter,
          apiaFilter: {
            ...currentFilter.apiaFilter,
            currentValue: '',
            savedValue: '',
            deleteFiltersTimestamp: Date.now(),
            options: currentFilter.apiaFilter.options.map((option) => {
              if (option.selected) {
                return {
                  ...option,
                  selected: false,
                };
              }
              return option;
            }),
          },
        };
        return newFilter;
      }
      if (
        currentFilter.apiaFilter.id === 'txtRelTitle' ||
        currentFilter.apiaFilter.id === 'txtInst'
      ) {
        const newFilter: TApiaFinderFilter = {
          ...currentFilter,
          apiaFilter: {
            ...currentFilter.apiaFilter,

            deleteFiltersTimestamp: Date.now(),
            currentValue: '',
            readonly: true,
          },
        };
        return newFilter;
      }
      return {
        ...currentFilter,
        apiaFilter: {
          ...currentFilter.apiaFilter,
          deleteFiltersTimestamp: Date.now(),
          currentValue: '',
          savedValue: '',
        },
      };
    }) ?? [];

  const payload: Record<string, unknown> = {
    action: 'clearFilter',
    ajaxUrl: state.ajaxUrl,
  };
  if (workingMode) {
    payload.workingMode = workingMode;
  }

  await ApiaApi.post<TApiaLoad<TApiaFunction>>(makeApiaUrl(payload));

  const areRequiredFilters =
    state.filters?.some((filter) => filter.apiaFilter.required) ?? false;

  if (areRequiredFilters) {
    return {
      filters: notifyRequiredFilters(listId, emptyFilters),
      areRequiredFilters,
    };
  }

  return { filters: emptyFilters };
});

export default deleteFilters;
